import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  TextField,
  ListSubheader,
} from '@mui/material';
import {
  AccountCircle,
  Logout,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Menu as MenuIcon,
  Inbox as InboxIcon,
  Clear as ClearIcon,
  Event as EventIcon,
  Favorite as FavoriteIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { chatApi, requestApi } from '../utils/api';
import { SportType } from '../types/event';
import { getAvatarProps } from '../utils/userUtils';
import SportsIcon from './SportsIcon';
import LoginModal from './auth/LoginModal';
import SignupModal from './auth/SignupModal';
import CreateEventModal from './CreateEventModal';
import NotificationBell from './NotificationBell';
import CompleteProfilePrompt from './CompleteProfilePrompt';

interface NavbarProps {
  onSearchChange?: (sport: string) => void;
  onEventCreated?: () => void;
  onProfileClick?: () => void;
  onInboxClick?: () => void;
  triggerSignup?: boolean;
  onSignupTriggered?: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ onSearchChange, onEventCreated, onProfileClick, onInboxClick, triggerSignup, onSignupTriggered }) => {
  const [loginOpen, setLoginOpen] = useState(false);
  const [signupOpen, setSignupOpen] = useState(false);
  const [createEventOpen, setCreateEventOpen] = useState(false);
  const [profilePromptOpen, setProfilePromptOpen] = useState(false);
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedSport, setSelectedSport] = useState<string>('');
  const [otherFilterMode, setOtherFilterMode] = useState(false);

  const { user, logout, isAuthenticated, isProfileComplete, needsProfileSetup, clearProfileSetup } = useAuth();
  const location_hook = useLocation();
  const navigate = useNavigate();

  // Check if we're on the Events page (home page)
  const isEventsPage = location_hook.pathname === '/';

  // Define available sports from the sportFileMap
  const availableSports: SportType[] = [
    'Tennis',
    'Badminton',
    'Golf',
    'Bouldering',
    'Pickleball',
    'Gym',
    'Hiking',
    'Ski/Snowboarding'
  ];

  // Notify parent component when search criteria changes
  useEffect(() => {
    if (onSearchChange) {
      onSearchChange(selectedSport);
    }
  }, [selectedSport, onSearchChange]);

  // Handle external signup trigger
  useEffect(() => {
    if (triggerSignup) {
      setSignupOpen(true);
      if (onSignupTriggered) {
        onSignupTriggered();
      }
    }
  }, [triggerSignup, onSignupTriggered]);

  // Nudge the user to complete their profile after signing up. Shows the
  // lightweight CompleteProfilePrompt; "Complete Profile" opens the editor.
  useEffect(() => {
    if (isAuthenticated && needsProfileSetup) {
      setProfilePromptOpen(true);
    }
  }, [isAuthenticated, needsProfileSetup]);

  const handleLogin = () => {
    setLoginOpen(true);
  };

  const handleSignUp = () => {
    setSignupOpen(true);
  };

  const handleSwitchToSignup = () => {
    setLoginOpen(false);
    setSignupOpen(true);
  };

  const handleSwitchToLogin = () => {
    setSignupOpen(false);
    setLoginOpen(true);
  };

  const handleSignupSuccess = () => {
    setSignupOpen(false);
    // Profile setup is now part of the signup flow
  };

  const handleCreateEvent = () => {
    if (!isProfileComplete) {
      setProfilePromptOpen(true);
      return;
    }
    setCreateEventOpen(true);
  };

  const handleCreateEventClose = () => {
    setCreateEventOpen(false);
  };

  const handleEventCreated = () => {
    // Trigger events refresh in parent component
    if (onEventCreated) {
      onEventCreated();
    }
    console.log('Event created successfully!');
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleLogout = async () => {
    await logout();
    handleMenuClose();
  };

  return (
    <>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: 'white',
          borderBottom: '1px solid #e3e3e3'
        }}
      >
        <Toolbar sx={{ 
          justifyContent: 'space-between', 
          px: { xs: 2, sm: 3, md: 4 }, 
          py: { xs: 1, sm: 1.5, md: 2 },
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          gap: { xs: 0.5, md: 0 }
        }}>
          {/* Logo */}
          <Box
            onClick={() => navigate('/')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              flexShrink: 0,
              // Desktop: hover effect
              '@media (hover: hover) and (pointer: fine)': {
                '&:hover': {
                  opacity: 0.8,
                  transition: 'opacity 0.2s ease'
                }
              },
              // Mobile: tap feedback
              '@media (hover: none) and (pointer: coarse)': {
                '&:active': {
                  opacity: 0.7,
                  transform: 'scale(0.97)'
                }
              }
            }}
          >
            <img
              src="/assets/logo.svg"
              alt="Shua"
              style={{
                height: '40px',
                width: 'auto',
                display: 'block'
              }}
            />
          </Box>

          {/* Center Section: Search (events page) + mobile Create Event */}
          {isEventsPage ? (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              flex: { xs: '1 1 100%', md: 1 },
              order: { xs: 3, md: 2 },
              width: { xs: '100%', md: 'auto' },
              justifyContent: 'center',
              mt: { xs: 0.5, md: 0 },
              position: { md: 'absolute' },
              left: { md: '50%' },
              transform: { md: 'translateX(-50%)' },
            }}>
              <TextField
                select
                label=""
                placeholder="Search sports"
                value={otherFilterMode ? '__other__' : selectedSport}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '__other__') {
                    setOtherFilterMode(true);
                    setSelectedSport('');
                  } else {
                    setOtherFilterMode(false);
                    setSelectedSport(v);
                  }
                }}
                sx={{
                  width: { xs: '60%', sm: 280, md: 320 },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '32px',
                    backgroundColor: '#ffffff',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.05)',
                    transition: 'box-shadow 0.2s ease, background-color 0.2s ease',
                    height: 56,
                    padding: '0 8px',
                    '& fieldset': {
                      borderColor: '#dddddd',
                      borderWidth: '1px'
                    },
                    '&:hover fieldset': {
                      borderColor: '#dddddd',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#dddddd',
                      borderWidth: '1px'
                    },
                    '&:hover': {
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1), 0 6px 16px rgba(0,0,0,0.08)',
                      backgroundColor: '#f7f7f7',
                    }
                  },
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                    py: 0,
                    height: '100%',
                    paddingLeft: '16px !important',
                    paddingRight: '56px !important',
                  }
                }}
                SelectProps={{
                  displayEmpty: true,
                  IconComponent: () => (
                    <Box sx={{ 
                      backgroundColor: '#533B7D', 
                      borderRadius: '50%', 
                      width: 40, 
                      height: 40, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      color: 'white',
                      pointerEvents: 'none',
                      position: 'absolute',
                      right: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      zIndex: 1
                    }}>
                      <SearchIcon sx={{ fontSize: 20 }} />
                    </Box>
                  ),
                  MenuProps: {
                    disableScrollLock: true,
                    PaperProps: {
                      sx: {
                        maxHeight: 400,
                        minWidth: 320,
                        borderRadius: '24px',
                        boxShadow: '0 12px 28px rgba(0,0,0,0.15)',
                        mt: 1,
                        p: 1,
                        '& .MuiMenuItem-root': {
                          minHeight: 64,
                          borderRadius: '12px',
                          mb: 0.5,
                          px: 2,
                          '&:hover': {
                            backgroundColor: '#f7f7f7'
                          }
                        }
                      }
                    },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                  },
                  renderValue: (value: unknown) => {
                    const isSelected = value && typeof value === 'string';
                    return (
                      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', width: '100%' }}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 800, color: '#222', lineHeight: 1.2 }}>
                          Sport
                        </Typography>
                        {isSelected && value === '__other__' ? (
                          <Typography sx={{ fontSize: '14px', color: '#717171', lineHeight: 1.2, mt: 0.25, fontWeight: 600 }}>
                            {selectedSport || 'Other'}
                          </Typography>
                        ) : isSelected ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#717171', fontSize: '14px', mt: 0.25 }}>
                            <SportsIcon sport={value as SportType} size={14} />
                            <Typography sx={{ fontSize: '14px', lineHeight: 1.2, color: '#717171', fontWeight: 600 }}>
                              {value}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography sx={{ fontSize: '14px', color: '#717171', lineHeight: 1.2, mt: 0.25 }}>
                            Search sports
                          </Typography>
                        )}
                      </Box>
                    );
                  }
                }}
                InputLabelProps={{
                  shrink: false,
                }}
                size="small"
              >
                <ListSubheader sx={{ 
                  fontSize: '12px', 
                  fontWeight: 600, 
                  color: '#222', 
                  lineHeight: '16px', 
                  py: 2, 
                  px: 2,
                  backgroundColor: 'transparent'
                }}>
                  Suggested sports
                </ListSubheader>
                <MenuItem value="" sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ 
                    width: 48, 
                    height: 48, 
                    borderRadius: '12px', 
                    backgroundColor: '#f7f7f7', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <SearchIcon sx={{ color: '#222' }} />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '15px', color: '#222' }}>
                      All Sports
                    </Typography>
                    <Typography sx={{ fontSize: '13px', color: '#717171' }}>
                      Find any event
                    </Typography>
                  </Box>
                </MenuItem>
                {availableSports.map((sport) => (
                  <MenuItem key={sport} value={sport} sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ 
                      width: 48, 
                      height: 48, 
                      borderRadius: '12px', 
                      backgroundColor: '#f7f7f7', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <SportsIcon sport={sport} size={24} style={{ color: '#222' }} />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '15px', color: '#222' }}>
                        {sport}
                      </Typography>
                      <Typography sx={{ fontSize: '13px', color: '#717171' }}>
                        Find {sport.toLowerCase()} events
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
                <MenuItem value="__other__" sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    backgroundColor: '#f7f7f7',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Typography sx={{ fontSize: 24 }}>➕</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '15px', color: '#222' }}>
                      Other
                    </Typography>
                    <Typography sx={{ fontSize: '13px', color: '#717171' }}>
                      Search a custom sport
                    </Typography>
                  </Box>
                </MenuItem>
              </TextField>

              {otherFilterMode && (
                <TextField
                  size="small"
                  placeholder="Type a sport"
                  value={selectedSport}
                  onChange={(e) => setSelectedSport(e.target.value)}
                  autoFocus
                  sx={{
                    width: { xs: '60%', sm: 200 },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '32px',
                      backgroundColor: '#ffffff',
                    },
                  }}
                />
              )}

              {(selectedSport || otherFilterMode) && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ClearIcon />}
                  onClick={() => { setSelectedSport(''); setOtherFilterMode(false); }}
                  sx={{
                    textTransform: 'none',
                    height: 48,
                    borderRadius: '24px',
                    px: { xs: 1.5, sm: 2.5 },
                    color: '#222',
                    borderColor: '#dddddd',
                    display: { xs: 'none', sm: 'flex' },
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: '#f7f7f7',
                      borderColor: '#dddddd'
                    },
                  }}
                >
                  Clear
                </Button>
              )}

              {/* Mobile-only Create Event beside search */}
              {isAuthenticated && user && (
                <Button
                  variant="contained"
                  onClick={handleCreateEvent}
                  startIcon={<AddIcon />}
                  sx={{
                    display: { xs: 'inline-flex', sm: 'none' },
                    backgroundColor: '#533B7D',
                    color: 'white',
                    fontWeight: 'bold',
                    textTransform: 'none',
                    borderRadius: 2,
                    px: 1.5,
                    height: 40,
                    fontSize: '0.875rem',
                    '&:hover': {
                      backgroundColor: '#422F64',
                    },
                    '& .MuiButton-startIcon': {
                      marginRight: 0.5
                    }
                  }}
                >
                  Create
                </Button>
              )}
            </Box>
          ) : (
            // Spacer for non-Events pages to maintain layout on desktop
            <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' } }} />
          )}

          {/* Auth Buttons / User Menu */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: { xs: 0.5, sm: 1, md: 1.5 },
            order: { xs: 2, md: 3 },
            flexShrink: 0
          }}>
            {isAuthenticated && user ? (
              <>
                <NotificationBell />

                <Avatar
                  {...getAvatarProps(user, 40)}
                  onClick={() => navigate('/profile')}
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: '#eaddff',
                    cursor: 'pointer',
                    display: { xs: 'none', sm: 'flex' },
                    transition: 'opacity 0.2s ease, transform 0.2s ease',
                    '&:hover': {
                      opacity: 0.8,
                      transform: 'scale(1.05)'
                    },
                    '&:active': {
                      transform: 'scale(0.95)'
                    },
                    ...getAvatarProps(user, 40).sx
                  }}
                />

                <Button
                  onClick={handleMenuOpen}
                  sx={{
                    minWidth: 'auto',
                    width: 40,
                    height: 40,
                    color: '#000',
                    position: 'relative'
                  }}
                >
                  <MenuIcon />
                </Button>

                <Button
                  variant="contained"
                  onClick={handleCreateEvent}
                  startIcon={<AddIcon />}
                  sx={{
                    display: isEventsPage ? { xs: 'none', sm: 'inline-flex' } : 'inline-flex',
                    backgroundColor: '#533B7D',
                    color: 'white',
                    fontWeight: 'bold',
                    textTransform: 'none',
                    borderRadius: 2,
                    px: { xs: 1.5, sm: 2 },
                    height: { xs: 40, md: 48 },
                    fontSize: { xs: '0.875rem', md: '1rem' },
                    '&:hover': {
                      backgroundColor: '#422F64',
                    },
                    '& .MuiButton-startIcon': {
                      marginRight: { xs: 0.5, sm: 1 }
                    }
                  }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    Create event
                  </Box>
                  <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                    Create
                  </Box>
                </Button>

                <Menu
                  anchorEl={menuAnchorEl}
                  open={Boolean(menuAnchorEl)}
                  onClose={handleMenuClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                >
                  <MenuItem onClick={() => { handleMenuClose(); onProfileClick && onProfileClick(); }}>
                    <AccountCircle sx={{ mr: 1 }} />
                    Profile
                  </MenuItem>
                  <MenuItem onClick={() => { handleMenuClose(); setEditProfileOpen(true); }}>
                    <EditIcon sx={{ mr: 1 }} />
                    Edit Profile
                  </MenuItem>
                  <MenuItem onClick={() => { handleMenuClose(); onInboxClick && onInboxClick(); }}>
                    <InboxIcon sx={{ mr: 1 }} />
                    Inbox
                  </MenuItem>
                  <MenuItem onClick={() => { handleMenuClose(); navigate('/my-events'); }}>
                    <EventIcon sx={{ mr: 1 }} />
                    My Events
                  </MenuItem>
                  <MenuItem onClick={() => { handleMenuClose(); navigate('/liked-events'); }}>
                    <FavoriteIcon sx={{ mr: 1 }} />
                    Liked Events
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={handleLogout}>
                    <Logout sx={{ mr: 1 }} />
                    Logout
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <>
                <Button
                  variant="text"
                  onClick={handleLogin}
                  sx={{
                    color: '#000',
                    fontWeight: 'bold',
                    textTransform: 'none'
                  }}
                >
                  Log in
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSignUp}
                  sx={{
                    backgroundColor: '#533B7D',
                    color: 'white',
                    fontWeight: 'bold',
                    textTransform: 'none',
                    borderRadius: 2,
                    px: 2,
                    '&:hover': {
                      backgroundColor: '#422F64',
                    }
                  }}
                >
                  Sign up
                </Button>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Authentication Modals */}
      <LoginModal
        open={loginOpen}
        onClose={() => setLoginOpen(false)}
        onSwitchToSignup={handleSwitchToSignup}
      />

      <SignupModal
        open={signupOpen}
        onClose={() => setSignupOpen(false)}
        onSwitchToLogin={handleSwitchToLogin}
        onSignupSuccess={handleSignupSuccess}
      />

      <CreateEventModal
        open={createEventOpen}
        onClose={handleCreateEventClose}
        onEventCreated={handleEventCreated}
      />

      <CompleteProfilePrompt
        open={profilePromptOpen}
        onClose={() => {
          setProfilePromptOpen(false);
          if (needsProfileSetup) clearProfileSetup();
        }}
        onComplete={() => {
          setProfilePromptOpen(false);
          setEditProfileOpen(true);
        }}
        actionLabel={needsProfileSetup ? 'get started' : 'create an event'}
      />

      <SignupModal
        open={editProfileOpen}
        onClose={() => {
          setEditProfileOpen(false);
          if (needsProfileSetup) clearProfileSetup();
        }}
        onSignupSuccess={() => {
          setEditProfileOpen(false);
          if (needsProfileSetup) {
            clearProfileSetup();
          } else {
            setCreateEventOpen(true);
          }
        }}
        editMode
        userData={user || undefined}
        title={needsProfileSetup ? 'Complete Your Profile' : 'Edit Profile'}
        contextMessage={needsProfileSetup ? "Add your sports and details to get started." : "Please complete your profile first to create an event."}
      />
    </>
  );
};

export default Navbar;
